import React, { useEffect, useMemo, useState } from "react";
import classes from "./OrganizationSettings.module.css";
import api from "../../Components/API/Api";
import { useLocation } from "react-router-dom";
import HeaderNav from "../../Components/Dahboard Header/HeaderNav/HeaderNav";
import { Tabs, Tab } from "react-bootstrap";
import WalletCard from "../../Assets/wall.svg";
import SavingsIcon from "../../Assets/tot-savings.svg";
import LoanIcon from "../../Assets/tot-loans.svg";
import RepaymentIcon from "../../Assets/repayments.svg";

const fallback = "—";

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return fallback;
  return value;
};

const formatWalletNumber = (value) => {
  if (!value) return "0000 0000 0000 0000";
  const compact = String(value).replace(/\s+/g, "");
  if (compact.length < 8 || /[^0-9]/.test(compact)) return String(value);
  return compact.replace(/(.{4})/g, "$1 ").trim();
};

const MembersProfile = () => {
  const location = useLocation();
  const customer = location.state?.customer;
  const memberId = customer?.id;

  const [savings, setSavings] = useState([]);
  const [loans, setLoans] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [memberDetails, setMemberDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [companyId] = useState(() => localStorage.getItem("company_id") || "");

  useEffect(() => {
    const fetchLedgers = async () => {
      if (!memberId) return;
      setRoleLoading(true);

      try {
        const res = await api.get("/v1/member/ledgers", {
          params: { member_id: memberId },
        });

        setLoans(Array.isArray(res?.data?.data?.loans) ? res.data.data.loans : []);
        setSavings(
          Array.isArray(res?.data?.data?.savings) ? res.data.data.savings : []
        );
      } catch (err) {
        console.error("Failed to fetch member ledgers", err);
      } finally {
        setRoleLoading(false);
      }
    };

    fetchLedgers();
  }, [memberId]);

  useEffect(() => {
    const fetchMemberDetails = async () => {
      if (!memberId || !companyId) return;
      setDetailsLoading(true);

      try {
        const res = await api.get("/v1/member/details", {
          params: { member_id: memberId, company_id: companyId },
        });

        const rawData = res?.data?.data;
        if (Array.isArray(rawData) && rawData.length > 0) {
          const member = rawData[0];
          setMemberDetails({
            ...member,
            wallet_balance: member.balance,
          });
        }
      } catch (err) {
        console.error("Failed to fetch member details", err);
      } finally {
        setDetailsLoading(false);
      }
    };

    fetchMemberDetails();
  }, [memberId, companyId]);

  const totalSavings = useMemo(
    () => savings.reduce((sum, item) => sum + parseFloat(item.balance || 0), 0),
    [savings]
  );

  const totalLoans = useMemo(
    () => loans.reduce((sum, item) => sum + parseFloat(item.balance || 0), 0),
    [loans]
  );

  const totalRepayments = useMemo(
    () => loans.reduce((sum, item) => sum + parseFloat(item.credit || 0), 0),
    [loans]
  );

  const displayData = useMemo(
    () => ({ ...customer, ...memberDetails }),
    [customer, memberDetails]
  );

  const formatMoney = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fullName =
    [displayData?.first_name, displayData?.other_name, displayData?.last_name]
      .filter(Boolean)
      .join(" ") || fallback;

  const initials = `${displayData?.last_name?.charAt(0) || ""}${
    displayData?.first_name?.charAt(0) || ""
  }`.toUpperCase();

  const walletNumber = formatWalletNumber(
    displayData?.wallet_number || displayData?.account_number
  );

  const metrics = [
    {
      label: "TOTAL SAVINGS",
      value: `₦${formatMoney(totalSavings)}`,
      icon: SavingsIcon,
      iconClass: classes.iconOrange,
      alt: "Total savings",
    },
    {
      label: "TOTAL LOANS",
      value: `₦${formatMoney(totalLoans)}`,
      icon: LoanIcon,
      iconClass: classes.iconBlue,
      alt: "Total loans",
    },
    {
      label: "TOTAL REPAYMENTS",
      value: `₦${formatMoney(totalRepayments)}`,
      icon: RepaymentIcon,
      iconClass: classes.iconPurple,
      alt: "Total repayments",
    },
  ];

  const personalRows = [
    { label: "Full Name", value: fullName },
    { label: "Member Number", value: displayData?.member_number },
    { label: "Gender", value: displayData?.gender },
    { label: "Phone Number", value: displayData?.phone_number },
    { label: "Email", value: displayData?.email },
    { label: "Address", value: displayData?.address },
  ];

  const nextOfKinRows = [
    { label: "Full Name", value: displayData?.next_of_kin_name },
    { label: "Relationship", value: displayData?.next_of_kin_relationship },
    { label: "Phone Number", value: displayData?.next_of_kin_phone },
  ];

  const guarantorRows = [
    { label: "Full Name", value: displayData?.guarantor_name },
    {
      label: "Address",
      value: displayData?.guarantor_address || displayData?.guarantor?.address,
    },
    { label: "Phone Number", value: displayData?.guarantor_phone },
  ];

  const savingsWithBalance = useMemo(() => {
    let runningBalance = 0;
    return savings.map((row) => {
      const debit = Number(row.debit || 0);
      const credit = Number(row.credit || 0);
      runningBalance = runningBalance + credit - debit;
      return {
        ...row,
        numericRunningBalance: runningBalance,
        runningBalance: formatMoney(runningBalance),
      };
    });
  }, [savings]);

  const loansWithBalance = useMemo(() => {
    let runningBalance = 0;
    return loans.map((row) => {
      const debit = Number(row.debit || 0);
      const credit = Number(row.credit || 0);
      runningBalance = runningBalance - debit + credit;
      return {
        ...row,
        numericRunningBalance: runningBalance,
        runningBalance: formatMoney(runningBalance),
      };
    });
  }, [loans]);

  const renderInfoRows = (rows) =>
    rows.map((row, index) => (
      <React.Fragment key={`${row.label}-${index}`}>
        <div className={classes.infoRow}>
          <span className={classes.label}>{row.label}</span>
          <span className={classes.value}>{displayValue(row.value)}</span>
        </div>
        {index < rows.length - 1 && <div className={classes.dividerLine} />}
      </React.Fragment>
    ));

  const renderLedgerRows = (entries, emptyMessage) => {
    if (roleLoading) {
      return (
        <tr>
          <td colSpan="7" className={classes.tableState}>
            Loading...
          </td>
        </tr>
      );
    }

    if (entries.length === 0) {
      return (
        <tr>
          <td colSpan="7" className={classes.tableState}>
            {emptyMessage}
          </td>
        </tr>
      );
    }

    return entries.map((item, idx) => (
      <tr key={item.id || item.uuid || idx}>
        <td>{idx + 1}</td>
        <td>{item.created_at?.split("T")[0] || fallback}</td>
        <td>{item.invoice_number || item.uuid || fallback}</td>
        <td>{item.description || fallback}</td>
        <td className={classes.numericCell}>{formatMoney(item.debit)}</td>
        <td className={classes.numericCell}>{formatMoney(item.credit)}</td>
        <td className={classes.numericCell}>{item.runningBalance}</td>
      </tr>
    ));
  };

  

  return (
    <>
      <HeaderNav />
      <main className={classes.container}>
        <section className={classes.topSectionGrid}>
          <div className={classes.leftColumn}>
            <div className={classes.profileHeaderWrapper}>
              <div className={classes.avatar}>{initials || "?"}</div>
              <div className={classes.headerInfo}>
                <h1 className={classes.fullName}>{fullName}</h1>
                <div className={classes.memberMeta}>
                  <span>{displayValue(displayData?.member_number)}</span>
                  <span className={classes.separator}>|</span>
                  <span>{displayValue(displayData?.email)}</span>
                </div>
              </div>
            </div>

            <div className={classes.metricsRow}>
              {metrics.map((metric) => (
                <div className={classes.metricCard} key={metric.label}>
                  <div className={classes.metricContent}>
                    <span className={classes.metricLabel}>{metric.label}</span>
                    <span className={classes.metricValue}>{metric.value}</span>
                  </div>
                  <div className={`${classes.metricIcon} ${metric.iconClass}`}>
                    <img src={metric.icon} alt={metric.alt} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <aside
            className={classes.walletBackground}
            style={{ backgroundImage: `url(${WalletCard})` }}
          >
            <div className={classes.walletContent}>
              <div className={classes.walletItem}>
                <span className={classes.walletLabel}>WALLET NUMBER</span>
                <span className={classes.walletValue}>{walletNumber}</span>
              </div>

              <div className={classes.walletItem}>
                <span className={classes.walletLabel}>AVAILABLE BALANCE</span>
                <span className={classes.walletValue}>
                  ₦{formatMoney(displayData?.wallet_balance || 0)}
                </span>
              </div>
            </div>
          </aside>
        </section>

        <section className={classes.tabsContainer}>
          <Tabs
            defaultActiveKey="overview"
            id="customer-details-tabs"
            className={classes.customTabs}
            mountOnEnter
            unmountOnExit={false}
          >
            <Tab eventKey="overview" title="Overview">
              <div className={classes.tabContent}>
                <div className={classes.informationGrid}>
                  <div className={classes.card}>
                    <h5 className={classes.cardTitle}>Personal Information</h5>
                    {renderInfoRows(personalRows)}
                  </div>

                  <div className={classes.card}>
                    <h5 className={classes.cardTitle}>Next of Kin</h5>
                    {renderInfoRows(nextOfKinRows)}

                    <div className={classes.subSection}>
                      <h6 className={classes.subTitle}>Guarantor</h6>
                      {renderInfoRows(guarantorRows)}
                    </div>
                  </div>

                  <div className={`${classes.card} ${classes.summaryCard}`}>
                    <h5 className={classes.cardTitle}>Financial Summary</h5>
                    <div className={classes.summaryTable}>
                      <div className={classes.summaryRow}>
                        <span>Total Savings</span>
                        <span>₦{formatMoney(totalSavings)}</span>
                      </div>
                      <div className={classes.summaryRow}>
                        <span>Total Loan Taken</span>
                        <span>₦{formatMoney(totalLoans)}</span>
                      </div>
                      <div className={classes.summaryRow}>
                        <span>Outstanding Loan</span>
                        <span>₦{formatMoney(totalLoans)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Tab>

            <Tab eventKey="savings" title="Savings Ledger">
              <div className={classes.tabContent}>
                <table className={classes.tables}>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Transaction Date</th>
                      <th>Invoice Number</th>
                      <th>Description</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* {renderLedgerRows(savings, "No savings records found.")} */}
                     {renderLedgerRows(savingsWithBalance, "No savings records found.")}
                  </tbody>
                </table>
              </div>
            </Tab>

            <Tab eventKey="loan" title="Loan Ledger">
              <div className={classes.tabContent}>
                <table className={classes.tables}>
                  <thead>
                    <tr>
                      <th>S/N</th>
                      <th>Transaction Date</th>
                      <th>Invoice Number</th>
                      <th>Description</th>
                      <th>Debit</th>
                      <th>Credit</th>
                      <th>Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                  {/* {renderLedgerRows(loans, "No loan records found.")} */}
                    {renderLedgerRows(loansWithBalance, "No loan records found.")}
                  </tbody>
                </table>
              </div>
            </Tab>

            <Tab eventKey="shares" title="Shares">
              <div className={classes.emptyState}>
                <h5>No Data Available</h5>
                <p>There are currently no share records.</p>
              </div>
            </Tab>

            <Tab eventKey="documents" title="Documents">
              <div className={classes.emptyState}>
                <h5>No Documents</h5>
                <p>No documents have been uploaded.</p>
              </div>
            </Tab>
          </Tabs>
        </section>
      </main>
    </>
  );
};

export default MembersProfile;
